/**
 * CenterOrderAlert — Apple-style center-screen notification fired
 * whenever an order is "put into process" (stock-checked → enters
 * Task Manager) or its godowns are assigned.
 *
 * Persistence model (2026-05-28 — cross-device sync):
 *   - On mount, fetches the user's PENDING (i.e. undismissed-by-this-
 *     user) alerts from the backend so a logged-out user gets every
 *     in-processing alert when they log in.
 *   - Every Pusher event is queued in the local stack AND every
 *     dismissal POSTs to `/api/task-manager/center-alerts/dismiss`
 *     so all of the user's other devices stop seeing it after they
 *     refresh (or call the pending endpoint on next focus).
 *   - "Never auto-dismiss" — the card stays on screen until the user
 *     clicks View Order or Dismiss.
 *
 * Recipient eligibility is decided server-side via
 * `GET /api/task-manager/me/notification-eligibility` and the same
 * filter is re-applied in `/center-alerts/pending`.
 *
 * Modal-immune rendering pattern: see App.js MutationObserver and the
 * `data-overlay-immune` attribute below.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Pusher from 'pusher-js';
import { envChannel } from '../utils/pusherEnv';
import { Package, X, Eye } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';

const PUSHER_KEY = 'cfc00fbbcf3d28bd5df1';
const PUSHER_CLUSTER = 'ap2';
const API = process.env.REACT_APP_BACKEND_URL;

const stepLabel = (step) => ({
  godown_assignment: 'Godown Assignment',
  packing: 'Packing',
  invoice_upload: 'Invoice Upload',
  lr_upload: 'LR Upload',
  doc_sharing: 'Document Sharing',
  completed: 'Completed',
}[step] || 'Task Manager');

// Map raw `source_location` from the backend payload to a short display
// label + colour scheme. Used on the in-processing toast so the
// operator can tell at a glance which warehouse split the order.
const locationMeta = (loc) => {
  if (loc === 'kashmere_gate') {
    return { label: 'Kashmere Gate', cls: 'bg-amber-100 text-amber-800 border-amber-200' };
  }
  if (loc === 'nangli_puna') {
    return { label: 'Nangli Puna', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' };
  }
  return null;
};

const formatINR = (n) => Number(n || 0).toLocaleString('en-IN');

export default function CenterOrderAlert() {
  const [alerts, setAlerts] = useState([]); // newest first
  const [eligibility, setEligibility] = useState(null);
  const pusherRef = useRef(null);
  const channelRef = useRef(null);
  const navigate = useNavigate();

  const fetchPending = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const res = await fetch(`${API}/api/task-manager/center-alerts/pending`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const list = Array.isArray(data.alerts) ? data.alerts : [];
      setAlerts((prev) => {
        // Merge: server list is the source of truth — pending items
        // we haven't seen yet are added at the top; locally queued
        // items still pending on the server stay; anything dismissed
        // (i.e. not in server list) drops off.
        const serverIds = new Set(list.map((a) => a.order_id));
        const stillLocal = prev.filter(
          (a) => !serverIds.has(a.order_id) && a._optimistic
        );
        return [...list, ...stillLocal].slice(0, 10);
      });
    } catch (_) { /* silent */ }
  }, []);

  // 1. Fetch eligibility once on mount (after token is in localStorage)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/task-manager/me/notification-eligibility`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setEligibility(data);
      } catch (_) { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // 2. Once eligible, hydrate from server + re-fetch on window focus
  useEffect(() => {
    if (!eligibility?.eligible) return;
    fetchPending();
    const onFocus = () => fetchPending();
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [eligibility, fetchPending]);

  // 3. Subscribe to Pusher channel only if eligible
  useEffect(() => {
    if (!eligibility?.eligible) return;

    const pusher = new Pusher(PUSHER_KEY, { cluster: PUSHER_CLUSTER });
    pusherRef.current = pusher;
    const channelName = envChannel('task-manager-broadcast');
    const channel = pusher.subscribe(channelName);
    channelRef.current = channel;

    const handler = (payload) => {
      if (!payload?.order_id) return;

      // Warehouse-role filter: only show if at least one involved
      // godown is in the user's assigned_godowns.
      if (eligibility.filter_by_godown) {
        const mine = new Set(eligibility.assigned_godowns || []);
        const involved = payload.involved_godown_ids || [];
        const hit = involved.some((g) => mine.has(g));
        if (!hit) return; // not for this warehouse user
      }

      setAlerts((prev) => {
        // De-dupe by order_id — the latest event for an order wins.
        const without = prev.filter((a) => a.order_id !== payload.order_id);
        return [{ ...payload, _optimistic: false }, ...without].slice(0, 10);
      });
    };
    channel.bind('new-order-in-processing', handler);

    return () => {
      try { channel.unbind('new-order-in-processing', handler); } catch {}
      try { pusher.unsubscribe(channelName); } catch {}
      try { pusher.disconnect(); } catch {}
    };
  }, [eligibility]);

  const dismiss = useCallback(async (orderId) => {
    // Optimistic local removal
    setAlerts((prev) => prev.filter((a) => a.order_id !== orderId));
    const token = localStorage.getItem('token');
    if (!token) return;
    // Persist dismissal server-side so other devices stop showing it.
    try {
      await fetch(`${API}/api/task-manager/center-alerts/dismiss`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ order_id: orderId }),
      });
    } catch (_) { /* silent — local removal already happened */ }
  }, []);

  const clearAll = useCallback(async () => {
    const ids = alerts.map((a) => a.order_id);
    setAlerts([]);
    const token = localStorage.getItem('token');
    if (!token || !ids.length) return;
    try {
      await fetch(`${API}/api/task-manager/center-alerts/clear-all`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (_) { /* silent */ }
  }, [alerts]);

  // Compute the right deep-link URL for an alert. Newer payloads
  // (from May-28+) already include `deep_link` server-side. Older
  // payloads (from earlier today, still in-flight on some screens)
  // don't — for those, we derive the URL client-side from
  // `current_step` so View Order behaves consistently regardless of
  // when the alert was fired.
  const computeDeepLink = useCallback((a) => {
    if (a?.deep_link) return a.deep_link;
    const step = a?.current_step || 'godown_assignment';
    const oid = a?.order_id;
    if (!oid) return '/task-manager';
    if (step === 'warehouse_allocation' || step === 'godown_assignment') {
      return `/task-manager/order/${oid}?tab=sorting`;
    }
    if (step === 'packing') {
      return `/task-manager/order/${oid}?tab=packing`;
    }
    // invoice_upload / lr_upload / doc_sharing / completed all live
    // on the dashboard's own tabs — fall back to dashboard with the
    // row scroll + pulse highlight.
    return `/task-manager?order=${encodeURIComponent(oid)}&step=${encodeURIComponent(step)}`;
  }, []);

  const viewOrder = useCallback((alert) => {
    const url = computeDeepLink(alert);
    dismiss(alert.order_id);
    navigate(url);
  }, [computeDeepLink, dismiss, navigate]);

  if (!eligibility?.eligible || alerts.length === 0) return null;

  // Show only the newest 3 as visible cards; remaining are tagged
  // as "+N more" on the bottom card.
  const visible = alerts.slice(0, 3);
  const overflow = Math.max(0, alerts.length - visible.length);

  const node = (
    <div
      data-overlay-immune="true"
      data-testid="center-order-alert-root"
      className="fixed inset-0 z-[100000] flex items-center justify-center pointer-events-none"
    >
      <div className="flex flex-col gap-3 pointer-events-auto w-[min(420px,92vw)]">
        {alerts.length >= 2 && (
          <div className="flex items-center justify-between text-[11px] font-medium text-white/95 bg-black/55 backdrop-blur-md rounded-full px-3 py-1 self-end">
            <span>{alerts.length} new in processing</span>
            <button
              onClick={clearAll}
              className="ml-3 underline hover:text-white"
              data-testid="center-alert-clear-all-btn"
            >
              Clear All
            </button>
          </div>
        )}
        {visible.map((a, idx) => {
          return (
            <div
              key={`${a.order_id}-${a.event_kind || 'k'}-${idx}`}
              data-testid={`center-alert-card-${a.order_id}`}
              className={[
                'relative bg-white/95 backdrop-blur-xl rounded-2xl border border-black/10',
                'shadow-[0_22px_70px_-12px_rgba(0,0,0,0.45)]',
                'animate-in zoom-in-95 fade-in slide-in-from-top-4 duration-300',
                'overflow-hidden',
              ].join(' ')}
              role="alertdialog"
              aria-modal="false"
            >
              {/* Top accent bar — Apple-style hairline */}
              <div className="h-[3px] w-full bg-gradient-to-r from-emerald-400 via-blue-500 to-violet-500" />
              <div className="p-5">
                <div className="flex items-start gap-3">
                  <div className="shrink-0 p-2.5 rounded-xl bg-emerald-50 border border-emerald-100">
                    <Package className="w-5 h-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-emerald-700">
                      New order in processing
                      {a.source && a.source !== 'domestic' && (
                        <Badge variant="secondary" className="text-[10px] py-0 px-1.5 capitalize">
                          {a.source}
                        </Badge>
                      )}
                    </div>
                    <div className="mt-1 text-[15px] font-semibold text-gray-900 truncate" title={a.party_name}>
                      {a.party_name}
                    </div>
                    <div className="text-xs text-gray-500 font-mono mt-0.5">
                      {a.order_id}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap text-[12px] text-gray-600">
                      <span>{new Date(a.order_date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}</span>
                      {a.final_amount > 0 && <span className="font-semibold text-gray-800">₹{formatINR(a.final_amount)}</span>}
                      <span className="px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-700 text-[10px] font-medium">
                        {stepLabel(a.current_step)}
                      </span>
                      {(() => {
                        const loc = locationMeta(a.source_location);
                        if (!loc) return null;
                        return (
                          <span
                            className={`px-1.5 py-0.5 rounded-full border text-[10px] font-semibold ${loc.cls}`}
                            data-testid={`center-alert-location-${a.order_id}`}
                          >
                            {loc.label}
                          </span>
                        );
                      })()}
                    </div>
                  </div>
                  <button
                    onClick={() => dismiss(a.order_id)}
                    className="shrink-0 p-1.5 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition"
                    data-testid={`center-alert-dismiss-x-${a.order_id}`}
                    aria-label="Dismiss"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="mt-4 flex items-center justify-end gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => dismiss(a.order_id)}
                    data-testid={`center-alert-dismiss-btn-${a.order_id}`}
                  >
                    Dismiss
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => viewOrder(a)}
                    className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700"
                    data-testid={`center-alert-view-btn-${a.order_id}`}
                  >
                    <Eye className="w-3.5 h-3.5 mr-1.5" />
                    View Order
                  </Button>
                </div>
                {idx === visible.length - 1 && overflow > 0 && (
                  <div className="mt-3 text-[11px] text-gray-500 text-center">
                    +{overflow} more order{overflow === 1 ? '' : 's'} in processing
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  return createPortal(node, document.body);
}

